// Vendor
import { FC, HTMLAttributes, useEffect } from 'react';

// Components
import { ButtonColor, ButtonVariant } from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { Row } from 'src/components/atoms/Row';
import Tag from 'src/components/atoms/Tag';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import {
  CompanyIcon,
  EditActionBox,
  EditIcon,
  HeaderTitleBox,
  PersonIcon,
  StyledButton,
  StyledCard,
  StyledText
} from './styles';

// Helpers
import { useSelector } from 'react-redux';
import { selectors as companySelector } from 'src/features/company/companySlice';
import {
  getEntryStatusColorTag,
  getResultStatusColorTag,
  startCase,
  useTransformResultToReadable
} from 'src/features/fdeInbox/helpers';
import { selectors as propertySelectors } from 'src/features/property/propertySlice';
import { ROUTE_NAMES } from 'src/router/navOptions';

// Types
import {
  EntryResultType,
  EntryStatusType,
  FolderResultType,
  FolderStatusType
} from 'src/ts/interfaces';

//Hooks
import Link from 'src/components/atoms/Link';
import { PUBLIC_URL } from 'src/components/atoms/NavItem';
import { GrayIconComponent } from 'src/components/atoms/NavItem/styles';
import SkeletonPiece, { SkeletonPieceColor } from 'src/components/atoms/SkeletonPiece';
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import useCompany from 'src/features/company/hooks/useCompany';
import useProperty from 'src/features/property/hooks/useProperty';
import useHasRole from 'src/features/users/hooks/useHasRole';
import { useNavOptionPermissions } from 'src/hooks';

export interface FolderTileInfoProps extends HTMLAttributes<HTMLDivElement> {
  onClickEdit: () => void;
  folderName: string;
  propertyName: string;
  propertyId?: string;
  companyId?: string;
  lastEntryStatus: EntryStatusType | FolderStatusType;
  lastEntryResult: EntryResultType | FolderResultType;
}

const FolderTileInfo: FC<FolderTileInfoProps> = (props) => {
  const {
    onClickEdit,
    propertyName,
    folderName,
    lastEntryStatus,
    lastEntryResult,
    companyId,
    propertyId,
    ...htmlAttrs
  } = props;
  const userCanViewPropertyPage = useHasRole(useNavOptionPermissions(ROUTE_NAMES.PROPERTIES));
  const userCanViewCompanyPage = useHasRole(useNavOptionPermissions(ROUTE_NAMES.COMPANIES));

  const { onGetById: onGetProperty } = useProperty();

  const getPropertyData = useSelector(propertySelectors.getOne.data);
  const getPropertyIsIdle = useSelector(propertySelectors.getOne.isIdle);

  useEffect(() => {
    if (getPropertyIsIdle && propertyId) {
      onGetProperty(propertyId);
    }
  }, [propertyId, onGetProperty, getPropertyIsIdle]);

  const { onGetOneById: onGetCompanyById } = useCompany();

  const companyData = useSelector(companySelector.getOne.data);
  const companyIsIdle = useSelector(companySelector.getOne.isIdle);
  const companyIsLoading = useSelector(companySelector.getOne.isLoading);
  useEffect(() => {
    if (!companyData || (companyId && companyData?.id !== companyId)) {
      onGetCompanyById(companyId || '');
    }
  }, [onGetCompanyById, companyId, companyData, companyIsIdle]);

  const { isAccountRepresentative } = useUserRoles();

  const transformResultToReadable = useTransformResultToReadable();

  return (
    <StyledCard rounded border {...htmlAttrs}>
      <Row>
        <HeaderTitleBox>
          <Row>
            <PersonIcon />
            <StyledText variant={TextVariant.span} color={TextColor.gray700}>
              {folderName}
            </StyledText>
          </Row>

          <Row>
            <CompanyIcon />
            <StyledText variant={TextVariant.span} color={TextColor.gray700}>
              <Link to={`/properties/${propertyId}/edit`} isDisabled={!userCanViewPropertyPage}>
                <Text color={TextColor.gray500}>
                  <div style={{ textDecoration: 'underline' }}>{propertyName}</div>
                </Text>
              </Link>
              {getPropertyData && (
                <Text color={TextColor.gray500}>
                  <div>
                    {getPropertyData?.address} <br />
                    {getPropertyData?.city}, {getPropertyData?.state} {getPropertyData?.zip}
                  </div>
                </Text>
              )}
            </StyledText>
          </Row>
        </HeaderTitleBox>

        {!isAccountRepresentative && (
          <EditActionBox>
            <StyledButton
              onClick={onClickEdit}
              name="Edit"
              variant={ButtonVariant.outline}
              color={ButtonColor.disabled}
            >
              Edit <EditIcon />
            </StyledButton>
          </EditActionBox>
        )}
      </Row>

      <Gap height={1} />

      {companyData && (
        <Row data-testid="LastReportEntryRow" justify="space-between">
          <Row>
            <GrayIconComponent src={`${PUBLIC_URL}/assets/images/icon-briefcase-white.svg`} />
            {companyIsLoading ? (
              <SkeletonPiece width={100} height={20} color={SkeletonPieceColor.gray50} isRounded />
            ) : (
              <StyledText variant={TextVariant.span} color={TextColor.gray700}>
                <Link to={`/companies/${companyId}/edit`} isDisabled={!userCanViewCompanyPage}>
                  <Text color={TextColor.gray500}>
                    <div style={{ textDecoration: 'underline' }}>{companyData?.name}</div>
                  </Text>
                </Link>
              </StyledText>
            )}
          </Row>
        </Row>
      )}

      <Gap height={2} />

      <Row data-testid="LastReportEntryRow" justify="space-between">
        <Row direction="column" gap={0.5}>
          <Row.Col>
            <Text color={TextColor.gray600} variant={TextVariant.small}>
              Last Report Entry Status
            </Text>
          </Row.Col>
          <Row.Col>
            <Tag
              label={startCase(lastEntryStatus as EntryStatusType)}
              color={getEntryStatusColorTag(lastEntryStatus as EntryStatusType)}
              showBackground
            />
          </Row.Col>
        </Row>

        <Row direction="column" gap={0.5}>
          <Row.Col>
            <Text color={TextColor.gray600} variant={TextVariant.small}>
              Last Report Entry Result
            </Text>
          </Row.Col>
          <Row.Col alignSelf="flex-end">
            <Tag
              id="last-report-entry-result-tag"
              label={transformResultToReadable(lastEntryResult as EntryResultType)}
              color={getResultStatusColorTag(lastEntryResult as EntryResultType)}
              showBackground
            />
          </Row.Col>
        </Row>
      </Row>
    </StyledCard>
  );
};

export default FolderTileInfo;
